import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether the referenced element's content overflows its (clamped) box.
 *
 * Re-measures on layout/resize (via ResizeObserver) and after web fonts load,
 * so it works even when the element is mounted while hidden (e.g. inside an
 * inactive tab or a not-yet-visible drawer) or before fonts are available.
 *
 * Measurement is paused while `enabled` is false (e.g. when the text is
 * expanded and the clamp is removed) so the previously detected value is kept.
 */
export default function useIsClamped(dependency, enabled = true) {
	const ref = useRef(null);
	const [isClamped, setIsClamped] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element || !enabled) {
			return undefined;
		}

		const measure = () => {
			setIsClamped(element.scrollHeight > element.clientHeight + 1);
		};

		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(element);

		let cancelled = false;
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(() => {
				if (!cancelled) {
					measure();
				}
			});
		}

		return () => {
			cancelled = true;
			observer.disconnect();
		};
	}, [dependency, enabled]);

	return [ref, isClamped];
}
